(() => {
  'use strict';

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex^="-"])',
  ].join(',');

  const getFocusableElements = (container) =>
    Array.from(container?.querySelectorAll(focusableSelector) ?? []).filter(
      (element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true',
    );

  const onEscape = (event, callback) => {
    if (event.key === 'Escape') callback(event);
  };

  window.FashionTheme = Object.freeze({
    events: new EventTarget(),
    getFocusableElements,
    onEscape,
  });

  document.addEventListener('click', (event) => {
    const sizeButton = event.target.closest('[data-fashion-size-picker] [data-variant-id]');
    if (!sizeButton || sizeButton.disabled) return;

    const picker = sizeButton.closest('[data-fashion-size-picker]');
    const card = picker.closest('.product-card-wrapper');
    if (!card) return;
    event.preventDefault();
    const variantId = sizeButton.dataset.variantId;

    card.querySelectorAll('.product-variant-id').forEach((input) => {
      input.value = variantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    card.querySelectorAll('[data-fashion-size-picker] [data-variant-id]').forEach((button) => {
      const selected = button.dataset.variantId === variantId;
      button.setAttribute('aria-current', String(selected));
      button.setAttribute('aria-pressed', String(selected));
    });
    card.querySelectorAll('a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;
      url.searchParams.set('variant', variantId);
      link.href = url.href;
    });
  });
})();
/* Product card previews: hover with a mouse, visible cards on touch screens. */
(() => {
  const touch = matchMedia('(hover: none), (pointer: coarse)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const states = new Map();
  const duration = 2200;

  const stop = (state) => {
    state.running = false;
    clearTimeout(state.timer);
    state.token++;
    state.index = 0;
    state.card.classList.remove('fashion-preview-running');
    state.image.classList.remove('is-visible');
    state.bars.forEach((bar) => bar.className = '');
  };

  const advance = async (state) => {
    if (!state.running || !state.card.isConnected) return stop(state);
    const token = ++state.token;
    const next = (state.index + 1) % state.urls.length;
    const preload = new Image();
    preload.src = state.urls[next];
    try { await preload.decode(); } catch { /* Keep current photo on network failure. */ }
    if (!state.running || token !== state.token) return;
    if (preload.naturalWidth) {
      state.image.src = preload.src;
      state.image.classList.add('is-visible');
      state.index = next;
    }
    state.bars.forEach((bar, i) => {
      bar.className = i < state.index ? 'is-complete' : i === state.index ? 'is-current' : '';
    });
    state.timer = setTimeout(() => advance(state), duration);
  };

  const start = (state) => {
    if (state.running || reduced.matches || document.hidden) return;
    state.running = true;
    state.card.classList.add('fashion-preview-running');
    state.bars[0].className = 'is-current';
    state.timer = setTimeout(() => advance(state), duration);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({target, isIntersecting, intersectionRatio}) => {
      const state = states.get(target);
      if (!state) return;
      state.visible = isIntersecting && intersectionRatio >= 0.65;
      if (!state.visible) stop(state);
      else if (touch.matches || state.hovered) start(state);
    });
  }, { threshold: [0, 0.65] });

  const setup = () => {
    states.forEach((state, element) => {
      if (!element.isConnected) { stop(state); observer.unobserve(element); states.delete(element); }
    });
    document.querySelectorAll('[data-fashion-preview-images]').forEach((data) => {
      const card = data.closest('.product-card-wrapper');
      const media = card?.querySelector('.card__media .media');
      if (!media || states.has(media)) return;
      const urls = JSON.parse(data.textContent);
      if (urls.length < 2) return;
      const image = document.createElement('img');
      image.className = 'fashion-preview-image';
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      const progress = document.createElement('div');
      progress.className = 'fashion-preview-progress';
      progress.setAttribute('aria-hidden', 'true');
      const bars = urls.map(() => progress.appendChild(document.createElement('span')));
      media.append(image);
      card.querySelector('.card__media').append(progress);
      card.classList.add('fashion-preview-ready');
      const state = { card, image, urls, bars, index: 0, token: 0, running: false, visible: false, hovered: false };
      states.set(media, state);
      card.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') { state.hovered = true; start(state); }
      });
      card.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') { state.hovered = false; stop(state); }
      });
      observer.observe(media);
    });
  };
  document.addEventListener('visibilitychange', () => states.forEach((s) => {
    if (document.hidden) stop(s);
    else if (s.visible && (touch.matches || s.hovered)) start(s);
  }));
  reduced.addEventListener('change', () => states.forEach((s) => { if (reduced.matches) stop(s); }));
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; setup(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  setup();
})();
